import { useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { BRAND_NOTE } from "@/data/content";

export default function SolutionModal({ solution, divisionLabel, onClose, onEnquire }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-[#0B0F19]/80 p-0 backdrop-blur-md sm:items-center sm:p-6"
      onClick={onClose}
      data-testid="solution-modal-overlay"
    >
      <motion.article
        role="dialog"
        aria-modal="true"
        aria-label={`${solution.title} details`}
        data-testid="solution-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 44, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 28, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        data-lenis-prevent
        className="glass-strong max-h-[92dvh] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-t-3xl shadow-2xl shadow-black/60 sm:rounded-3xl"
      >
        <div className="relative h-52 sm:h-64">
          <img src={solution.img} alt={`${solution.title} technology`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent" />
          <button
            data-testid="solution-modal-close"
            onClick={onClose}
            aria-label="Close solution details"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition-colors duration-300 hover:bg-brand"
          >
            <X size={18} />
          </button>
          <div className="absolute bottom-4 left-6 right-6">
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-brand">{divisionLabel} Solution</p>
            <h3 className="mt-1 font-heading text-2xl font-bold text-white sm:text-3xl">{solution.title}</h3>
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <div>
            <p className="mb-3 font-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Key highlights
            </p>
            <ul className="grid gap-2.5 sm:grid-cols-2">
              {solution.points.map((p) => (
                <li key={p} className="flex items-center gap-2.5 rounded-xl border border-slate-700/70 bg-white/[0.03] px-4 py-3 text-xs font-medium leading-relaxed text-slate-200 sm:text-sm">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                  {p}
                </li>
              ))}
            </ul>
            {solution.controls && (
              <p className="mt-3 text-xs italic text-slate-400">{solution.controls}</p>
            )}
          </div>

          <div className="mt-7">
            <p className="mb-3 font-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Brands we work with
            </p>
            <div className="flex flex-wrap gap-2">
              {solution.brands.length > 0 ? (
                solution.brands.map((b) => (
                  <span
                    key={b}
                    className="rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 text-xs font-semibold text-orange-300"
                  >
                    {b}
                  </span>
                ))
              ) : (
                <span className="rounded-full border border-slate-600 px-4 py-1.5 text-xs text-slate-300">
                  Curated from leading OEM partners
                </span>
              )}
            </div>
          </div>

          <p className="mt-7 rounded-xl border border-slate-700/80 bg-white/[0.03] p-4 text-xs leading-relaxed text-slate-400">
            {BRAND_NOTE}
          </p>

          <button
            data-testid="solution-modal-enquire"
            onClick={() => onEnquire(solution)}
            className="mt-6 w-full rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-hover hover:shadow-lg hover:shadow-brand/30"
          >
            Enquire About This Solution
          </button>
        </div>
      </motion.article>
    </motion.div>
  );
}
